'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

interface BlogPost {
  id: string;
  category: string;
  date: string;
  readTime: string;
  title: string;
  excerpt: string;
  href: string;
  featured?: boolean;
}

const blogPosts: BlogPost[] = [
  {
    id: 'ai-localization-2026',
    category: 'AI & Localization',
    date: 'March 28, 2026',
    readTime: '12 min read',
    title: 'AI Localization in 2026: How Enterprises Scale with Human-Verified AI',
    excerpt: 'Artificial Intelligence has become the operational backbone of global communication. The challenge is no longer speed; it is how enterprises scale language without losing brand integrity, cultural accuracy, or regulatory control. At this scale, human verification is no longer a safety net. It is the essential quality control layer of a high-speed, AI-driven assembly line.',
    href: '/blog/ai-localization-2026',
    featured: true,
  },
  {
    id: 'training-translation-global-workforce',
    category: 'Enterprise Training',
    date: 'March 15, 2026',
    readTime: '10 min read',
    title: 'Training Translation: How Localized Employee Training Transforms Global Workforce Performance',
    excerpt: 'Organizations still push out "global" training courses in one language and hope everyone follows along. In compliance, safety, or customer-facing roles, that comprehension gap quickly turns into risk. Localized employee training sits at the heart of how global companies keep people aligned, competent, and compliant.',
    href: '/blog/training-translation-global-workforce',
    featured: true,
  },
  {
    id: 'chinese-brands-localization-strategy',
    category: 'Global Strategy',
    date: 'March 5, 2026',
    readTime: '9 min read',
    title: 'Localization Strategy of Chinese Brands: Temu, Huawei, Xiaomi & BYD',
    excerpt: 'Over the past decade, Chinese brands have shifted from challengers to leaders across Asia, Europe, and the Americas. One factor appears consistently in their global playbooks: market-by-market localization. A well-planned localization strategy lifts conversion, reduces support friction, and de-risks launches in an era where a single misstep can go viral.',
    href: '/blog/chinese-brands-localization-strategy',
    featured: true,
  },
  {
    id: 'grow-business-globally-localization-strategy',
    category: 'Global Strategy',
    date: 'November 3, 2022',
    readTime: '7 min read',
    title: 'Grow Your Business Globally with a Strong Localization Strategy',
    excerpt: 'Home to almost 60% of the world\'s population, Asia has millions of people speaking different languages. Every successful business strives to achieve global excellence - but while going global, they must engage as a local. Brands need a localization strategy that covers not just text, but audio and video content too.',
    href: '/blog/grow-business-globally-localization-strategy',
  },
  {
    id: 'website-localization-strategy',
    category: 'Digital Strategy',
    date: 'November 3, 2022',
    readTime: '7 min read',
    title: 'Your Strategy for Global Success Starts with Website Localization',
    excerpt: 'Today, entering a global market is much easier to accomplish than in previous eras. Many businesses utilize their website to reach their target audience in international markets. However, the main challenge remains: how do you make your website truly resonate with local audiences?',
    href: '/blog/website-localization-strategy',
  },
  {
    id: 'brand-message-translation-dos-donts-part2',
    category: 'Translation for Marketing',
    date: 'October 15, 2022',
    readTime: '7 min read',
    title: "Part 2: Do's and Don'ts for Translating a Successful Brand Message",
    excerpt: 'What happens when your brand message is delivered incorrectly to global markets? From Pepsi\'s ancestor resurrection to HSBC\'s "Do Nothing" campaign, poor translation has derailed major brands. Learn from these epic fails and discover how to protect your brand message across cultures.',
    href: '/blog/brand-message-translation-dos-donts-part2',
  },
  {
    id: 'brand-message-translation-dos-donts-part1',
    category: 'Translation for Marketing',
    date: 'October 10, 2022',
    readTime: '6 min read',
    title: "Part 1: Do's and Don'ts for Translating a Successful Brand Message",
    excerpt: 'Your brand message is the story your company tells the world. Translating it effectively requires more than word-for-word conversion - it demands cultural intelligence, native expertise, and sometimes full transcreation. Here are the essential dos and don\'ts every global brand should know.',
    href: '/blog/brand-message-translation-dos-donts-part1',
  },
  {
    id: 'game-localization-part1',
    category: 'Game Localization',
    date: 'September 20, 2022',
    readTime: '8 min read',
    title: 'Game Localization: Essential Information to Know Before You Begin (Part 1)',
    excerpt: 'The mobile gaming market is valued at $64.7bn USD in Asia alone. Missing out on this massive market due to ineffective localization would be a costly mistake. Game localization is far more than translation - it\'s about delivering an experience that feels native to every player worldwide.',
    href: '/blog/game-localization-part1',
  },
  {
    id: 'game-localization-part2',
    category: 'Game Localization',
    date: 'September 25, 2022',
    readTime: '8 min read',
    title: 'Game Localization: Essential Information to Know Before You Begin (Part 2)',
    excerpt: 'From NES Pro-Wrestling\'s infamous "The winner is you" to PUBG\'s censorship crisis in China and COD: Modern Warfare 2\'s Russian translation disaster - game localization failures are costly. Here\'s what you must consider before localizing your game for global markets.',
    href: '/blog/game-localization-part2',
  },
  {
    id: 'elearning-localization-7-steps',
    category: 'eLearning Translation',
    date: 'May 4, 2022',
    readTime: '8 min read',
    title: '7 Important Steps to Consider for Your eLearning Localization',
    excerpt: 'eLearning has become an important part of many people\'s lives, especially since the pandemic. If you want to make your eLearning platform truly international, certain factors need to be carefully considered when translating and converting your courses for global audiences.',
    href: '/blog/elearning-localization-7-steps',
  },
  {
    id: 'mobile-app-translation-guide',
    category: 'Digital Strategy',
    date: 'April 11, 2022',
    readTime: '6 min read',
    title: 'A Brief Guide to Mobile App Translation',
    excerpt: 'Did you know that 84% of the world\'s population owns a smartphone? Smartphones have transformed our lives, and mobile apps are at the center of that transformation. If you want to reach global users, mobile app translation is no longer optional - it\'s essential for growth.',
    href: '/blog/mobile-app-translation-guide',
  },
  {
    id: 'multilingual-voice-over-perfect-voice',
    category: 'Multilingual Voice Over',
    date: 'March 15, 2022',
    readTime: '7 min read',
    title: 'How to Find the Perfect Voice for Your Multilingual Voice Over',
    excerpt: 'When it comes to multilingual voiceovers, you need a voice and accent to match your content and brand. Research shows it takes listeners just 30 milliseconds to form an opinion about a speaker\'s accent. Choosing the right voice can make or break your global content strategy.',
    href: '/blog/multilingual-voice-over-perfect-voice',
  },
  {
    id: 'elearning-translation-asia-6-keys',
    category: 'eLearning Translation',
    date: 'February 20, 2022',
    readTime: '7 min read',
    title: 'The 6 Keys to Effective eLearning Translation in Asia',
    excerpt: 'Asia is the world\'s largest and fastest-growing eLearning market. But translating eLearning content for Asian audiences requires more than linguistic accuracy - it demands deep cultural understanding, technical expertise, and a nuanced approach to the region\'s diverse languages and learning styles.',
    href: '/blog/elearning-translation-asia-6-keys',
  },
  {
    id: 'marketing-strategies-china-2019',
    category: 'China',
    date: 'December 10, 2019',
    readTime: '8 min read',
    title: '8 Marketing Strategies to Successfully Enter the Chinese Market',
    excerpt: 'With over 700 million internet users, China is the world\'s largest digital market. But operating in China doesn\'t come without its pitfalls. Here are 8 expert marketing strategies for successfully entering and growing in the Chinese market, shared by leading China market specialists.',
    href: '/blog/marketing-strategies-china-2019',
  },
  {
    id: 'southeast-asia-strategy-brands',
    category: 'Global Strategy',
    date: 'November 5, 2019',
    readTime: '9 min read',
    title: 'Why Brands Need a Southeast Asian Strategy',
    excerpt: 'Southeast Asia is a fast-growing eCommerce market driving greater need for localization services. Recent investments in infrastructure, improved regulatory conditions, and rapid growth in internet and smartphone penetration are opening up these markets for foreign brands like never before.',
    href: '/blog/southeast-asia-strategy-brands',
  },
  {
    id: 'advertising-transcreation-examples',
    category: 'Translation for Marketing',
    date: 'October 1, 2019',
    readTime: '7 min read',
    title: 'Top 8 Best Advertising Transcreation Examples',
    excerpt: 'What is transcreation and how does it differ from translation? While literal translation can wreck even the largest company\'s marketing strategy, transcreation adapts your message to resonate culturally. Here are 8 exceptional examples of advertising transcreation done brilliantly.',
    href: '/blog/advertising-transcreation-examples',
  },
  {
    id: 'smartcat-productivity-hacks',
    category: 'Translation Tools',
    date: 'December 3, 2022',
    readTime: '6 min read',
    title: 'Top 12 Productivity Hacks for Using Smartcat',
    excerpt: 'Smartcat is a great tool for translators: cloud-based, intuitive, and free. Like any tool, it has features that are not directly obvious but can make a big difference in productivity. Here are the hidden features that make Smartcat a power tool for professional translators.',
    href: '/blog/smartcat-productivity-hacks',
  },
  {
    id: 'top-22-translator-websites',
    category: 'Translation Tools',
    date: 'November 3, 2022',
    readTime: '8 min read',
    title: 'Top 22 Websites Every Translator Must Know',
    excerpt: 'A curated list of the best communities, dictionaries, tech tools, and productivity resources for professional translators. From ProZ and LinkedIn to DeepL and Smartcat, these are the sites every translator should have bookmarked.',
    href: '/blog/top-22-translator-websites',
  },
  {
    id: 'gamified-elearning-localization',
    category: 'eLearning Translation',
    date: 'November 3, 2022',
    readTime: '7 min read',
    title: 'How Businesses Succeed by Using Localized Gamified E-Learning Content',
    excerpt: 'The stratospheric increase in distance learning over the COVID-19 pandemic is fuelling the gamification of more and more education and training content. Find out how to localize gamified e-learning content effectively for global audiences.',
    href: '/blog/gamified-elearning-localization',
  },
  {
    id: 'arabic-localization-guide',
    category: 'Global Strategy',
    date: 'November 3, 2022',
    readTime: '7 min read',
    title: 'How to Successfully Localize and Translate Your Business into Arabic',
    excerpt: 'Arabic is widely spoken across 22 countries and is the native tongue of more than 200 million people worldwide. It ranks sixth among the major languages of the world and is one of the six official languages of the United Nations. Here\'s what you need to know before expanding into Arabic-speaking markets.',
    href: '/blog/arabic-localization-guide',
  },
  {
    id: 'french-localization-culture',
    category: 'Global Strategy',
    date: 'November 3, 2022',
    readTime: '6 min read',
    title: 'Reasons Why Culture Is Important to Localization: French',
    excerpt: 'In this series on cultural localization, we open with French and why a localized marketing strategy is needed based on where a language is spoken. French is spoken by 300 million people across 50+ countries, and each market requires its own cultural adaptation.',
    href: '/blog/french-localization-culture',
  },
  {
    id: 'blockchain-localization',
    category: 'Crypto & Blockchain',
    date: 'November 3, 2022',
    readTime: '6 min read',
    title: 'How to Succeed in Blockchain with Localization',
    excerpt: 'What does blockchain have to do with localization? More than you might think. Blockchain technology is inherently global, and for crypto projects to succeed worldwide, they need to communicate effectively with users, investors, and regulators in every market they operate in.',
    href: '/blog/blockchain-localization',
  },
  {
    id: 'crypto-document-translation',
    category: 'Crypto & Blockchain',
    date: 'November 3, 2022',
    readTime: '6 min read',
    title: 'Translating Blockchain and Crypto Documents the Right Way!',
    excerpt: 'Both blockchain and cryptocurrencies remain among the hottest topics in the business world. Translating their documents, including whitepapers, smart contract documentation, and legal filings, requires specialist expertise that combines technical knowledge with linguistic precision.',
    href: '/blog/crypto-document-translation',
  },
  {
    id: 'translation-services-global-business',
    category: 'Business Translation',
    date: 'November 3, 2022',
    readTime: '7 min read',
    title: 'How Can Translation Services Help You Create a Successful Global Business?',
    excerpt: 'Translating marketing materials and legal documents has become indispensable for businesses to expand worldwide. Read on to understand how multilingual translation services can help your business grow globally and what to look for in a translation partner.',
    href: '/blog/translation-services-global-business',
  },
];

const categories = [
  'All',
  'AI & Localization',
  'Enterprise Training',
  'Global Strategy',
  'Digital Strategy',
  'eLearning Translation',
  'Translation Tools',
  'Crypto & Blockchain',
  'Business Translation',
  'Translation for Marketing',
  'Game Localization',
  'Multilingual Voice Over',
  'China',
];

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredPosts = activeCategory === 'All'
    ? blogPosts
    : blogPosts.filter(p => p.category === activeCategory);

  const featuredPosts = blogPosts.filter(p => p.featured);
  const showFeatured = activeCategory === 'All';

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* ===== HERO ===== */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-cobalt/5 via-background to-background" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-8">
          <span className="text-cobalt font-mono text-sm tracking-widest uppercase mb-4 block">
            Insights & Thought Leadership
          </span>
          <h1 className="font-display font-bold text-5xl lg:text-6xl text-silver-text leading-tight mb-6 max-w-3xl">
            The Into23 Intelligence Blog
          </h1>
          <p className="text-lg text-silver-muted max-w-2xl leading-relaxed">
            Expert perspectives on AI localization, enterprise language operations, global expansion strategy, and the future of multilingual AI.
          </p>
        </div>
      </section>

      {/* ===== FEATURED POSTS ===== */}
      {showFeatured && (
        <section className="py-12 lg:py-16">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
            <h2 className="font-display font-bold text-2xl text-silver-text mb-8">Featured Articles</h2>
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Large featured card */}
              <Link href={featuredPosts[0]?.href ?? '#'} className="lg:col-span-2 group">
                <div className="glow-card rounded-2xl p-8 flex flex-col h-full">
                  <div className="flex items-center gap-3 mb-5">
                    <span className="px-3 py-1 rounded-full bg-cobalt/10 border border-cobalt/20 text-cobalt-glow text-xs font-mono">
                      {featuredPosts[0]?.category}
                    </span>
                    <span className="text-xs text-silver-muted">{featuredPosts[0]?.date}</span>
                    <span className="text-xs text-silver-muted">· {featuredPosts[0]?.readTime}</span>
                  </div>
                  <h3 className="font-display font-bold text-2xl text-silver-text mb-4 leading-snug group-hover:text-cobalt-glow transition-colors">
                    {featuredPosts[0]?.title}
                  </h3>
                  <p className="text-silver-muted leading-relaxed flex-1 mb-6">
                    {featuredPosts[0]?.excerpt}
                  </p>
                  <span className="inline-flex items-center gap-2 text-cobalt-glow text-sm font-medium">
                    Read Article
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m9 18 6-6-6-6" />
                    </svg>
                  </span>
                </div>
              </Link>

              {/* Stacked smaller featured cards */}
              <div className="flex flex-col gap-6">
                {featuredPosts.slice(1, 3).map((post) => (
                  <Link key={post.id} href={post.href} className="group flex-1">
                    <div className="glow-card rounded-xl p-6 flex flex-col h-full">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="px-2.5 py-0.5 rounded-full bg-cobalt/10 border border-cobalt/20 text-cobalt-glow text-xs font-mono">
                          {post.category}
                        </span>
                      </div>
                      <h3 className="font-display font-semibold text-base text-silver-text mb-2 leading-snug line-clamp-2 group-hover:text-cobalt-glow transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-xs text-silver-muted line-clamp-2 mb-3 flex-1">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-silver-muted">{post.date}</span>
                        <span className="text-xs text-cobalt-glow">{post.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ===== ALL POSTS ===== */}
      <section className="py-12 lg:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          {/* Category filter */}
          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeCategory === cat
                    ? 'bg-cobalt text-white' :'bg-graphite border border-steel/30 text-silver-muted hover:text-silver-text hover:border-cobalt/30'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Posts grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts.map((post) => (
              <Link key={post.id} href={post.href} className="group">
                <div className="glow-card rounded-xl p-7 flex flex-col h-full">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="px-3 py-1 rounded-full bg-cobalt/10 border border-cobalt/20 text-cobalt-glow text-xs font-mono">
                      {post.category}
                    </span>
                  </div>
                  <h3 className="font-display font-semibold text-lg text-silver-text mb-3 leading-snug line-clamp-2 group-hover:text-cobalt-glow transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-sm text-silver-muted leading-relaxed mb-5 flex-1 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-steel/20">
                    <span className="text-xs text-silver-muted">{post.date}</span>
                    <span className="text-xs text-cobalt-glow font-medium">{post.readTime}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-20">
              <p className="text-silver-muted text-lg">No articles found in this category.</p>
              <button
                onClick={() => setActiveCategory('All')}
                className="mt-4 text-cobalt-glow hover:text-cobalt text-sm font-medium transition-colors"
              >
                View all articles
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="glow-card rounded-2xl p-10 lg:p-16 text-center">
            <span className="text-cobalt font-mono text-sm tracking-widest uppercase mb-4 block">
              Stay Informed
            </span>
            <h2 className="font-display font-bold text-3xl lg:text-4xl text-silver-text mb-4">
              Ready to Scale Your Language Operations?
            </h2>
            <p className="text-silver-muted max-w-xl mx-auto mb-8 leading-relaxed">
              Whether you need AI translation at scale, LLM evaluation, or a complete enterprise language program, Into23 has the technology and expertise to deliver.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href="/get-a-quote"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-cobalt text-white font-semibold rounded-lg hover:bg-cobalt/90 transition-colors"
              >
                Get a Quote
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </Link>
              <Link
                href="/case-studies"
                className="inline-flex items-center gap-2 px-8 py-3.5 border border-steel/30 text-silver-muted hover:text-silver-text hover:border-cobalt/30 font-medium rounded-lg transition-colors"
              >
                View Case Studies
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
